import { useFormikContext, Form } from 'formik'
import { ArrowRightIcon, Button, Grid, H, P, Wrapper } from '@farewill/ui'

import CheckboxGroup from 'components/form/checkbox-group'
import DatetimePicker from 'components/form/datetime-picker'
import Input from 'components/form/input'
import SelectInput from 'components/form/select-input'
import PartnerSelect from 'components/partner-select'
import type { Option } from 'components/form/select-input/types'
import { ACTIVE_BRANDS } from 'config/brands'
import type { BrandId } from 'config/brands'
import { AircallEventAttributes } from 'lib/models/aircall-event'
import { isOption } from 'lib/types/isOption'
import {
  LEAD_SOURCE_TYPE_OPTIONS,
  LEAD_SOURCE_IDENTIFIER_OPTIONS,
  LEAD_PARTNER_TYPE_OPTIONS,
  LEAD_SOURCE_TYPES,
} from 'utils/enums'
import { getAvailableProductsForBrand, showPartnerSelect } from 'utils/helpers'
import { NewContactFormValues } from '../types'
import { useMemo } from 'react'
import { SelectInputOptions } from 'components/form/select-input/types'

const isSingleBrandSelected = (
  e: Option | SelectInputOptions | null
): e is Option => {
  return e !== null && !Array.isArray(e)
}

const NewContactForm = ({
  call,
}: {
  call: Partial<AircallEventAttributes>
}) => {
  const formik = useFormikContext<NewContactFormValues>()
  const { values, isSubmitting, setValues } = formik

  const isPartnership = values.partnerType === 'other_partner'
  const callSelected = call.id
  const sourceType = values.sourceType
  const sourceTypeHasSourceIdentifier = (
    sourceType: string
  ): sourceType is keyof typeof LEAD_SOURCE_IDENTIFIER_OPTIONS => {
    return sourceType in LEAD_SOURCE_IDENTIFIER_OPTIONS
  }

  const sourceIdentifierOptions = sourceTypeHasSourceIdentifier(sourceType)
    ? LEAD_SOURCE_IDENTIFIER_OPTIONS[sourceType]
    : undefined

  const availableProducts = useMemo(() => {
    return getAvailableProductsForBrand(values.brandId as BrandId)
  }, [values.brandId])

  return (
    <Form>
      <Grid>
        <Grid.Item span={6}>
          <H size="S" margin="0">
            New lead
          </H>
          <Wrapper margin={['M', 0]}>
            <SelectInput
              name="brandId"
              label="Brand"
              options={ACTIVE_BRANDS}
              onChange={(e) => {
                if (isSingleBrandSelected(e)) {
                  setValues((prevValues) => ({
                    ...prevValues,
                    brandId: e.value as BrandId,
                  }))
                } else {
                  throw new Error('Expected a single brand')
                }
              }}
            />
          </Wrapper>
        </Grid.Item>
        {values.brandId && (
          <Grid.Item>
            <CheckboxGroup
              name="interestedInProducts"
              options={availableProducts}
              label={values.brandId ? 'Product' : ''}
            />
            {values.brandId && availableProducts.length === 0 && (
              <P>No products available for this brand</P>
            )}
          </Grid.Item>
        )}

        {!callSelected && (
          <Grid.Item>
            <Grid>
              <Grid.Item span={6}>
                <SelectInput
                  label="Type"
                  name="sourceType"
                  options={LEAD_SOURCE_TYPE_OPTIONS}
                />
              </Grid.Item>
              {sourceType === LEAD_SOURCE_TYPES.branch_referral ? (
                <Grid.Item span={6}>
                  <Input
                    label="Compass ID"
                    name="sourceIdentifier"
                    type="text"
                  />
                </Grid.Item>
              ) : (
                sourceIdentifierOptions && (
                  <Grid.Item span={6}>
                    <SelectInput
                      label="Source"
                      name="sourceIdentifier"
                      options={sourceIdentifierOptions}
                    />
                  </Grid.Item>
                )
              )}
            </Grid>
            <Grid margin={['L', 0]}>
              <Grid.Item span={6}>
                <SelectInput
                  label="Partner type"
                  name="partnerType"
                  onChange={(e) => {
                    const value =
                      isOption(e) && typeof e.value === 'string' ? e.value : ''
                    return setValues((prevValues) => ({
                      ...prevValues,
                      notes: '',
                      referredBy: '',
                      scheduledNextCallAt: '',
                      sourceIdentifier: '',
                      partnerId: '',
                      partnerType: value,
                    }))
                  }}
                  options={LEAD_PARTNER_TYPE_OPTIONS}
                />
              </Grid.Item>
              {showPartnerSelect(values.partnerType) && (
                <Grid.Item span={6}>
                  <PartnerSelect
                    label="Partner Name"
                    name="partnerId"
                    partnerType={values.partnerType}
                  />
                </Grid.Item>
              )}
            </Grid>
            <Grid>
              {isPartnership && (
                <>
                  <Grid.Item span={6}>
                    <Input
                      label="Referred by"
                      name="referredBy"
                      hint="The name of the person at the partner company who owns this lead"
                    />
                  </Grid.Item>
                  <Grid.Item span={6}>
                    <DatetimePicker
                      label="Scheduled callback time"
                      name="scheduledNextCallAt"
                      hint="Leave this blank if a call hasn't been scheduled with the customer"
                    />
                  </Grid.Item>
                  <Grid.Item>
                    <Input
                      label="Notes"
                      name="notes"
                      component="textarea"
                      rows={4}
                    />
                  </Grid.Item>
                </>
              )}
            </Grid>
          </Grid.Item>
        )}
        <Grid.Item>
          <Button.Primary
            icon={<ArrowRightIcon inline />}
            type="submit"
            disabled={isSubmitting}
          >
            Create lead
          </Button.Primary>
        </Grid.Item>
      </Grid>
    </Form>
  )
}

export default NewContactForm
